import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './NLMDemoPage.css';

const PHASES = [
  {
    name: 'Phonics',
    status: 'training',
    desc: '70 phonograms. 29 spelling rules. What letters are, what sounds they make, how many sounds each phonogram has.',
    examples: ['What is a?', 'How many sounds does ch make?', 'What are vowels?', 'What phonogram has the most sounds?', 'What are the ei exceptions?', 'What is the FLOSS rule?'],
  },
  {
    name: 'Word Types',
    status: 'training',
    desc: 'Nouns, verbs, adjectives, adverbs, pronouns, prepositions, conjunctions.',
    examples: ['What is a noun?', 'What is a verb?', 'What is an adjective?'],
  },
  {
    name: 'Morphology',
    status: 'training',
    desc: 'Prefixes, suffixes, roots. How words are built.',
    examples: ['What is the 1-1-1 rule with examples?'],
  },
  {
    name: 'Grammar',
    status: 'training',
    desc: 'Sentence structure. Subject-verb agreement. Sentence types.',
    examples: [],
  },
  {
    name: 'Comprehension',
    status: 'training',
    desc: 'Reading comprehension. Context routing. Understanding meaning.',
    examples: [],
  },
  {
    name: 'Thalamic Router',
    status: 'training',
    desc: 'The orchestrator. Decides which module handles each input.',
    examples: [],
  },
  {
    name: 'Lexicon',
    status: 'next',
    desc: 'Word definitions linked to emotional state. Coming next.',
    examples: [],
  },
  {
    name: 'Context Awareness',
    status: 'planned',
    desc: 'Clinical vs. casual vs. creative. Same word, different meaning by domain.',
    examples: [],
  },
];

function NLMDemoPage() {
  const [question, setQuestion] = useState('');
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedModule, setSelectedModule] = useState('phonics');
  const [showHistory, setShowHistory] = useState(false);

  // Load public history on mount
  useEffect(() => {
    fetch('/api/reading-brain/history')
      .then(r => r.json())
      .then(data => setHistory(data.history || []))
      .catch(() => {});
  }, []);

  const askBrain = async (q) => {
    const query = q || question;
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await fetch('/api/reading-brain/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: query, module: selectedModule }),
      });
      const data = await res.json();
      setResults(prev => [data, ...prev]);
      // Refresh history
      fetch('/api/reading-brain/history')
        .then(r => r.json())
        .then(d => setHistory(d.history || []))
        .catch(() => {});
    } catch (err) {
      setResults(prev => [{
        question: query,
        answer: 'The Reading Brain is currently offline. It may be resting between training sessions.',
        module_used: 'offline',
        routing: null,
        timestamp: new Date().toISOString(),
      }, ...prev]);
    }
    setLoading(false);
    setQuestion('');
  };

  const handlePreset = (q) => {
    setQuestion(q);
    askBrain(q);
  };

  return (
    <div className="nlm-demo">
      <div className="nlm-demo-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/hero-bg.png)` }}></div>
      <div className="nlm-demo-overlay"></div>

      {/* History Sidebar */}
      <div className={`nlm-history-sidebar ${showHistory ? 'open' : ''}`}>
        <div className="nlm-history-header">
          <h3>Public Query Log</h3>
          <p>Every question ever asked. Every answer ever given. Nothing hidden.</p>
          <button className="nlm-history-close" onClick={() => setShowHistory(false)}>&times;</button>
        </div>
        <div className="nlm-history-list">
          {history.length === 0 && <p className="nlm-history-empty">No queries yet. Be the first to ask.</p>}
          {history.map((h, i) => (
            <div key={i} className="nlm-history-item">
              <div className="nlm-history-time">{h.timestamp || 'unknown'}</div>
              <div className="nlm-history-q">{h.question}</div>
              <div className="nlm-history-module">{h.module_used}</div>
              <div className="nlm-history-a">{h.answer}</div>
            </div>
          ))}
        </div>
      </div>

      {/* History Toggle Button */}
      <button className="nlm-history-toggle" onClick={() => setShowHistory(!showHistory)}>
        {showHistory ? 'Close Log' : `Query Log (${history.length})`}
      </button>

      <main className="nlm-demo-main">
        {/* Header */}
        <div className="nlm-demo-header">
          <div className="nlm-demo-badge">LIVE &middot; RAW OUTPUT &middot; NO FILTER</div>
          <h1>Aether Reading Brain</h1>
          <p className="nlm-demo-subtitle">
            A neurochemical AI learning to read from scratch. Built on Scyla.
            What you get back is <strong>computation</strong>, not text generation &mdash;
            so it can be wrong, but it can&rsquo;t hallucinate.
          </p>
        </div>

        {/* What you're talking to */}
        <div className="nlm-demo-notice">
          <h3>This is a baby.</h3>
          <p>
            The model is learning to read the way a child learns to read &mdash; phonics first,
            then words, then grammar, then comprehension. It will be wrong. It will be messy.
            That&rsquo;s what learning looks like.
          </p>
          <p>
            Unlike conventional AI, when this one is wrong it doesn&rsquo;t make something up.
            It mis-routes the question to the wrong piece of compiled knowledge &mdash; and you
            can see exactly which one. That&rsquo;s the point.
          </p>
          <p>
            <strong>Every query and every answer is logged publicly</strong> on the
            <Link to="/nlm-log" style={{ color: 'var(--gold, #c9a84c)', marginLeft: '0.25rem' }}>training journal</Link>.
            Nothing is cleaned up. Nothing is hidden. Hiding misses would be the actual lie.
          </p>
        </div>

        {/* Training Progress */}
        <div className="nlm-demo-progress">
          <h2>Training Phases</h2>
          <div className="nlm-demo-phases">
            {PHASES.map((phase, i) => (
              <div key={i} className={`nlm-demo-phase ${phase.status}`}>
                <div className="phase-header">
                  <span className="phase-name">{phase.name}</span>
                  <span className={`phase-status ${phase.status}`}>
                    {phase.status === 'training' ? 'IN TRAINING' :
                     phase.status === 'next' ? 'NEXT' : 'PLANNED'}
                  </span>
                </div>
                <p className="phase-desc">{phase.desc}</p>
                {phase.examples.length > 0 && (
                  <div className="phase-examples">
                    {phase.examples.map((ex, j) => (
                      <button key={j} className="phase-example-btn" onClick={() => handlePreset(ex)}>
                        {ex}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Stats - no percentages */}
        <div className="nlm-demo-stats">
          <div className="nlm-stat"><span className="nlm-stat-value">14.2M</span><span className="nlm-stat-label">Parameters</span></div>
          <div className="nlm-stat"><span className="nlm-stat-value">5</span><span className="nlm-stat-label">Modules</span></div>
          <div className="nlm-stat"><span className="nlm-stat-value">H-H</span><span className="nlm-stat-label">Gating</span></div>
          <div className="nlm-stat"><span className="nlm-stat-value">9D</span><span className="nlm-stat-label">Neurochemical</span></div>
          <div className="nlm-stat"><span className="nlm-stat-value">$4.34</span><span className="nlm-stat-label">Training Cost</span></div>
        </div>

        {/* Ask */}
        <div className="nlm-demo-ask">
          <h2>Ask the Reading Brain</h2>
          <p className="nlm-demo-ask-note">
            Raw output. No post-processing. No cleanup. What the model produces is what you see.
            It will be wrong sometimes. That is honesty.
          </p>
          <div className="nlm-demo-input-area">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && askBrain()}
              placeholder="Ask the Reading Brain..."
              className="nlm-demo-input"
            />
            <select value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)} className="nlm-demo-select">
              <option value="auto">Auto (Router)</option>
              <option value="phonics">Phonics</option>
              <option value="compound-lm">Compound LM (next-token)</option>
              <option value="word_type">Word Types</option>
              <option value="morphology">Morphology</option>
              <option value="grammar">Grammar</option>
              <option value="comprehension">Comprehension</option>
            </select>
            <button onClick={() => askBrain()} disabled={loading} className="nlm-demo-btn">
              {loading ? '...' : 'Ask'}
            </button>
          </div>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="nlm-demo-results">
            {results.map((r, i) => (
              <div key={i} className="nlm-demo-result">
                <div className="result-q">{r.question}</div>
                <div className="result-module">{r.module_used}</div>
                <div className="result-a">{r.answer}</div>
                {r.routing && <div className="result-routing">{Object.entries(r.routing).map(([k,v]) => `${k}: ${v}`).join(' | ')}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Patent */}
        <div className="nlm-demo-patent">
          <p>Patent Pending: U.S. Provisional Application Nos. 63/939,190 &middot; 63/962,385 &middot; 63/988,485</p>
          <p>Nexus Concordat Inc.</p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#444' }}>
            No base model. No pretrained weights. 100% Neurochemical Cascade Architecture.
          </p>
        </div>
      </main>
    </div>
  );
}

export default NLMDemoPage;
